import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

const MAX_VIDEOS = 5;

const videos = [];
for (let i = 1; i <= MAX_VIDEOS; i++) {
  const url = process.env[`REACT_APP_HOME_FEATURED_VIDEO_${i}_URL`];
  if (!url) break;
  videos.push({
    url,
    title: process.env[`REACT_APP_HOME_FEATURED_VIDEO_${i}_TITLE`] || "",
    thumb: process.env[`REACT_APP_HOME_FEATURED_VIDEO_${i}_THUMB`] || "",
    image: process.env[`REACT_APP_HOME_FEATURED_VIDEO_${i}_IMAGE`] || "",
  });
}

export default function FeaturedVideoSection() {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const displayTitle = t("home.videotitle", "Featured Videos");

  // Desktop: measure the video box's actual rendered width and derive its 16:9
  // height from that, instead of CSS aspect-ratio + flex stretch — the image
  // then uses this same number directly, so it can never end up taller than
  // the video (which was leaving a gap/black bar under the video).
  const videoBoxRef = useRef(null);
  const [measuredVideoHeight, setMeasuredVideoHeight] = useState(0);

  useEffect(() => {
    const el = videoBoxRef.current;
    if (!el || isMobile) return;
    const update = () => setMeasuredVideoHeight(Math.round((el.offsetWidth * 9) / 16));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile]);

  if (videos.length === 0) return null;

  const selected = videos[selectedIndex];
  const primaryColor = theme.palette.primary.main;
  const thumbWidth = isMobile ? 115 : 220;
  const thumbHeight = isMobile ? 70 : 130;
  const videoHeight = isMobile ? 220 : measuredVideoHeight;

  return (
    <Box sx={{ width: "100%", px: isMobile ? 2 : 0, mt: isMobile ? 2 : 0 }}>
      {displayTitle && (
        <Typography
          sx={{
            color: primaryColor,
            fontWeight: "bold",
            fontSize: isMobile ? "16px" : "20px",
            // mb: 1.5,
          }}
        >
          {displayTitle}
        </Typography>
      )}

      {/* Row 1: video + image (desktop) or video only (mobile) */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "flex-start",
        }}
      >
        <Box ref={videoBoxRef} sx={{ flex: isMobile ? 1 : "0 0 60%", minWidth: 0 }}>
          {/* Fills its share of the row width; height is the exact 16:9 value
              computed above, so this box never grows past the video itself. */}
          <iframe
            width="100%"
            height={videoHeight || undefined}
            src={selected.url}
            title={selected.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            style={{ display: "block" }}
          />
        </Box>

        {!isMobile && selected.image && (
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", borderRadius: 1 }}>
            {/* Same explicit height as the video (measured above); width follows the image's aspect ratio. */}
            <img
              src={selected.image}
              alt={selected.title}
              style={{ height: videoHeight || "100%", width: "auto", maxWidth: "100%", objectFit: "contain", display: "block" }}
            />
          </Box>
        )}
      </Box>

      {/* Row 2: scrollable thumbnail selector — hidden when only 1 video */}
      {videos.length > 1 && <Box
        sx={{
          display: "flex",
          gap: 1,
          width: "100%",
          overflowX: "auto",
          pb: isMobile ? 0.5 : 1,
          ...(isMobile
            ? {
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }
            : {
                "&::-webkit-scrollbar": { height: "6px"},
                "&::-webkit-scrollbar-track": { background: "#111" },
                "&::-webkit-scrollbar-thumb": { background: primaryColor, borderRadius: "2px" },
              }),
        }}
      >
        {videos.map((v, idx) => {
          const isSelected = idx === selectedIndex;
          return (
            <Box
              key={idx}
              onClick={() => setSelectedIndex(idx)}
              sx={{
                flex: `0 0 ${thumbWidth}px`,
                cursor: "pointer",
                borderRadius: 1,
                overflow: "hidden",
                border: isSelected ? `2px solid ${primaryColor}` : "2px solid transparent",
                background: "#1a1a1a",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: thumbHeight,
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <img
                  src={v.thumb}
                  alt={v.title}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    filter: isSelected ? "none" : "grayscale(100%)",
                    transition: "filter 0.2s",
                  }}
                />
              </Box>
              {v.title && (
                <Typography
                  sx={{
                    fontSize: isMobile ? "10px" : "12px",
                    color: isSelected ? primaryColor : "white",
                    px: 0.5,
                    py: 0.5,
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: 1.3,
                  }}
                >
                  {v.title}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>}
    </Box>
  );
}
